import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import ClientLayout from '../../components/layout/ClientLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { FileText, Upload, Trash2, Download, Paperclip } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function ClientDocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reqId, setReqId] = useState('general');
  const fileRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [dRes, rRes] = await Promise.all([
        axios.get(`${API}/client/documents`),
        axios.get(`${API}/client/staffing-requests`).catch(() => ({ data: [] })),
      ]);
      setDocs(dRes.data || []);
      setRequests((rRes.data || []).filter((r) => r.status === 'active'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      if (reqId !== 'general') fd.append('request_id', reqId);
      fd.append('file', file);
      await axios.post(`${API}/client/documents`, fd);
      toast.success('Document uploaded — our team has been notified');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (doc) => {
    try {
      await axios.delete(`${API}/client/documents/${doc.id}`);
      toast.success('Document deleted');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const download = async (doc) => {
    try {
      const res = await axios.get(`${API}/client/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.file_name || 'document'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <ClientLayout>
      <div className="space-y-4" data-testid="client-documents-page">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-zinc-900 tracking-tight">Documents</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Upload RAMS, site instructions or any file for our team. PDF, Word, JPG, PNG — max 10 MB.</p>
        </div>

        <Card className="bg-white border-zinc-200 rounded-lg shadow-sm">
          <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="space-y-1.5 flex-1">
              <Label className="text-xs uppercase tracking-wider">Attach to</Label>
              <Select value={reqId} onValueChange={setReqId}>
                <SelectTrigger className="rounded-md" data-testid="doc-request-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General (not linked to a request)</SelectItem>
                  {requests.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.site_name} ({r.category})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
              data-testid="doc-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="h-10 bg-[#14181f] text-white hover:bg-[#232a35] rounded-md"
              data-testid="doc-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading…' : 'Upload Document'}
            </Button>
          </CardContent>
        </Card>

        {loading ? (
          <p className="py-10 text-center text-sm text-zinc-500">Loading…</p>
        ) : docs.length === 0 ? (
          <Card className="bg-white border-zinc-200 rounded-lg shadow-sm">
            <CardContent className="p-10 text-center space-y-2">
              <FileText className="w-10 h-10 mx-auto text-zinc-300" />
              <p className="text-sm text-zinc-500" data-testid="docs-empty">No documents yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => (
              <Card key={d.id} className="bg-white border-zinc-200 rounded-lg shadow-sm" data-testid={`client-doc-${d.id}`}>
                <CardContent className="p-3.5 flex items-center gap-3">
                  <span className="w-9 h-9 rounded-md bg-zinc-100 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-zinc-600" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-900 truncate">{d.file_name}</p>
                    <div className="text-xs text-zinc-500 flex flex-wrap items-center gap-x-2">
                      <span>{fmtSize(d.file_size || 0)}</span>
                      <span>· {(() => { try { return format(parseISO(d.created_at), 'dd MMM yyyy HH:mm'); } catch { return ''; } })()}</span>
                      {d.request_site && (
                        <Badge variant="outline" className="text-[10px] border-zinc-300 text-zinc-600">
                          <Paperclip className="w-2.5 h-2.5 mr-0.5" />{d.request_site}
                        </Badge>
                      )}
                      <Badge variant="outline" className={`text-[10px] ${d.uploaded_by === 'admin' ? 'border-blue-300 text-blue-600' : 'border-zinc-300 text-zinc-500'}`}>
                        {d.uploaded_by === 'admin' ? 'From S.A.N team' : 'Uploaded by you'}
                      </Badge>
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => download(d)} className="h-8 w-8 text-zinc-500 hover:text-zinc-900" data-testid={`download-doc-${d.id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                  {d.uploaded_by === 'client' && (
                    <Button size="icon" variant="ghost" onClick={() => remove(d)} className="h-8 w-8 text-zinc-400 hover:text-red-500" data-testid={`delete-doc-${d.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
